/**
 * O texto do "Recalcular CENA" — o resumo do desfecho, o antes → depois da
 * nota e as notas de cadastro. Puro e testado, como o `ai-reprocess-view.ts`:
 * os componentes só desenham.
 */
import type { CenaRecalcItem, CenaRecalcRun } from "@/shared/api/api-client";
import { formatCena } from "@/shared/utils/format";
import {
  CENA_RECALC_NOTE_LABELS,
  CENA_RECALC_PART_LABELS,
  type CenaRecalcNote,
  type CenaRecalcPart
} from "@/shared/vocabulary/cena-recalc-parts";

/** A execução ainda vai andar? */
export function isCenaRecalcRunning(run: CenaRecalcRun): boolean {
  return run.status === "QUEUED" || run.status === "STARTED";
}

const pt = (value: number) => value.toLocaleString("pt-BR");

function count(value: number, singular: string, plural: string): string {
  return `${pt(value)} ${value === 1 ? singular : plural}`;
}

/** "Recálculo concluído: X mudaram de nota, Y ficaram iguais" mais, só
 *  quando maior que zero, as ressalvas. */
export function recalcSummary(run: CenaRecalcRun): string {
  const head = `Recálculo concluído: ${count(run.changed, "mudou", "mudaram")} de nota, ${count(
    run.unchanged,
    "ficou igual",
    "ficaram iguais"
  )}`;
  const extras: string[] = [];
  if (run.notes.vehicle_not_found > 0) {
    extras.push(`${pt(run.notes.vehicle_not_found)} sem veículo no cadastro`);
  }
  if (run.notes.channel_not_found > 0) {
    extras.push(`${pt(run.notes.channel_not_found)} sem canal no cadastro`);
  }
  if (run.notes.no_format_data > 0) {
    extras.push(`${pt(run.notes.no_format_data)} sem dado de formato`);
  }
  if (run.conflicts > 0) {
    extras.push(`${pt(run.conflicts)} alteradas durante o recálculo`);
  }
  if (run.failed > 0) {
    extras.push(`${pt(run.failed)} ${run.failed === 1 ? "falhou" : "falharam"}`);
  }
  return extras.length ? `${head} · ${extras.join(" · ")}.` : `${head}.`;
}

/** "ICena 5,2 → 6,8" — `null` é sem nota ("—"), nunca zero. */
export function icenaChange(item: Pick<CenaRecalcItem, "icena_before" | "icena_after">): string {
  return `ICena ${formatCena(item.icena_before)} → ${formatCena(item.icena_after)}`;
}

export function partLabels(parts: readonly CenaRecalcPart[]): string[] {
  return parts.map(part => CENA_RECALC_PART_LABELS[part] ?? part);
}

export function noteLabels(notes: readonly CenaRecalcNote[]): string[] {
  return notes.map(note => CENA_RECALC_NOTE_LABELS[note] ?? note);
}
